// OnboardingPermissions
// =====================
//
// Wave 14ca-fix8: Kombinierter Permission-Onboarding-Screen.
//
// User-Wunsch:
//   "die Frage soll schon beim Installieren der Karten am Anfang kommen,
//    mit der Botschaft dass nur dann das fehlerfreie Funktionieren der
//    App gewährleistet wird, auch gleich für Location und Kompass-Nutzung,
//    dann wird der User später nicht damit konfrontiert."
//
// Der Screen sitzt im Mount-Tree zwischen AuthGate und FirstLaunchGate
// (siehe main.tsx). Heißt: User loggt sich ein → Permissions abfragen →
// Maps werden runtergeladen. So sind alle Berechtigungen vor dem ersten
// echten App-Use geklärt.
//
// Drei Permissions, alle in einem Sheet:
//   1. Notifications (Web Push)        — neue Trails/Runs/Touren
//   2. Location (Geolocation)          — Position auf Karte, "from here"-
//                                         Routenplanung
//   3. Motion & Orientation (Compass)  — Karte dreht sich mit Handy-Heading
//
// Pro Permission:
//   - 'pending'      → "Grant"-Button (ruft die echte API auf)
//   - 'granted'      → grünes Häkchen, "Granted"-Label
//   - 'denied'       → rotes ✕, "Denied — fix in browser/iOS settings"
//   - 'unsupported'  → grauer Strich, "Not available on this device"
//
// "Continue"-Button am Ende: setzt das localStorage-Flag damit der
// Screen nie wieder kommt — auch wenn der User Permissions skippt.
// Re-Aktivierung läuft über die Profile-Seite.

import { useEffect, useState, type ReactNode } from 'react';
import { deviceHeadingBus } from '../lib/map/deviceHeadingBus';
import { isPushSupported, subscribe as pushSubscribe } from '../lib/push';

const DONE_KEY = 'adt_onboarding_perms_done';

type PermStatus = 'pending' | 'granted' | 'denied' | 'unsupported';

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone =
    (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  const displayStandalone = window.matchMedia('(display-mode: standalone)').matches;
  return iosStandalone || displayStandalone;
}

function isMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  if (/Android/i.test(ua)) return true;
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return true;
  return false;
}

function compassNeedsPermission(): boolean {
  // Nur iOS 13+ verlangt eine explizite Permission. Auf Android/Desktop
  // gibt's kein requestPermission() — Sensor ist da oder eben nicht.
  const AnyOrient = (
    typeof window === 'undefined' ? null : (window as unknown as Record<string, unknown>).DeviceOrientationEvent
  ) as { requestPermission?: () => Promise<'granted' | 'denied'> } | null;
  return Boolean(AnyOrient && typeof AnyOrient.requestPermission === 'function');
}

export function OnboardingPermissions({ children }: { children: ReactNode }) {
  const [needsScreen, setNeedsScreen] = useState(false);
  const [push, setPush] = useState<PermStatus>('pending');
  // Wave 14ca-fix64: Konkrete Fehler-Message bei Push-Subscribe-Fail.
  // Vorher wurde JEDER Fehler (no_public_key, backend_failed, …) auf
  // status='denied' gemappt — der User sieht "denied" und denkt er hat
  // selbst auf "ablehnen" getippt, dabei sind die VAPID-Keys auf dem
  // Server gar nicht konfiguriert. Jetzt zeigen wir die tatsaechliche
  // Ursache als kleinen Hint unter der Push-Karte.
  const [pushError, setPushError] = useState<string | null>(null);
  const [location, setLocation] = useState<PermStatus>('pending');
  const [compass, setCompass] = useState<PermStatus>('pending');

  useEffect(() => {
    // Skip wenn schon durch (auch nach Re-Install — localStorage übersteht
    // PWA-Reinstall solange der User Storage nicht manuell killt).
    try {
      if (window.localStorage.getItem(DONE_KEY) === '1') return;
    } catch {
      /* Privacy-Mode → wir zeigen lieber zu oft als zu selten */
    }

    // Nur auf Mobile + Standalone zeigen. Desktop-Browser brauchen keine
    // dieser Permissions in der Form, und im Mobile-Tab geht Push eh nicht.
    if (!isMobile() || !isStandalone()) {
      // KEIN localStorage setzen — falls der User später vom Homescreen
      // öffnet, soll er den Screen sehen.
      return;
    }

    setNeedsScreen(true);

    // Initial-Status für Push einlesen.
    if (!isPushSupported()) {
      setPush('unsupported');
    } else if (typeof Notification !== 'undefined') {
      const p = Notification.permission;
      if (p === 'granted') setPush('granted');
      else if (p === 'denied') setPush('denied');
      // sonst bleibt 'pending'
    }

    // Initial-Status für Location: keine read-only API auf Safari, also
    // bleibt 'pending' bis User klickt. navigator.permissions.query
    // funktioniert auf iOS Safari nicht zuverlässig — deshalb nicht.
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocation('unsupported');
    }

    // Initial-Status für Compass.
    if (typeof window === 'undefined' || typeof window.DeviceOrientationEvent === 'undefined') {
      setCompass('unsupported');
    } else if (!compassNeedsPermission()) {
      // Android/Desktop: keine Permission nötig, Sensor ist da → granted.
      setCompass('granted');
    }
  }, []);

  if (!needsScreen) return <>{children}</>;

  const handlePush = async () => {
    // Wave 14ca-fix64: 'denied' lassen wir klickbar (User kann nach
    // Browser-Setting-Anpassung nochmal probieren), darum hier auch retry
    // erlauben.
    if (push === 'granted' || push === 'unsupported') return;
    setPushError(null);
    try {
      await pushSubscribe();
      setPush('granted');
    } catch (err) {
      const code = err instanceof Error ? err.message : 'unknown';
      console.warn('[onboarding] push subscribe failed', code);
      // Differenzierte Fehlerbehandlung statt globalem 'denied':
      //   - permission_denied → User hat im Browser-Prompt abgelehnt; das ist
      //     ein echter denied-State, Toggle wird rot.
      //   - no_public_key / backend_failed / subscribe_failed / unsupported →
      //     liegt NICHT am User. Wir bleiben auf 'pending' (Toggle laesst
      //     sich nochmal antippen) und zeigen einen verstaendlichen Hint.
      if (code === 'permission_denied') {
        setPush('denied');
        setPushError(
          'Notifications were blocked. You can re-enable them in browser settings.',
        );
      } else if (code === 'unsupported') {
        setPush('unsupported');
        setPushError(
          'Notifications are not supported on this device/browser.',
        );
      } else if (code === 'no_public_key') {
        setPushError(
          'Notifications are not configured on the server yet — try again later or enable them via Profile.',
        );
      } else if (code === 'backend_failed') {
        setPushError(
          'Server rejected the subscription. Check your network and try again.',
        );
      } else if (code === 'subscribe_failed') {
        setPushError(
          'Browser couldn’t complete the subscription. Try again, or check that the app is installed to your home screen (iOS).',
        );
      } else {
        setPushError(`Could not enable notifications (${code}). Try again.`);
      }
    }
  };

  const handleLocation = () => {
    if (location !== 'pending') return;
    if (!navigator.geolocation) {
      setLocation('unsupported');
      return;
    }
    // getCurrentPosition triggert den iOS-Permission-Prompt. Success-
    // Callback heißt "granted"; Fehler-Code 1 (PERMISSION_DENIED) heißt
    // "denied". Andere Fehler (timeout, unavailable) sind nicht
    // permission-bezogen — wenn der User schon zugestimmt hat aber GPS
    // grad keinen Fix hat, ist die Permission trotzdem da.
    navigator.geolocation.getCurrentPosition(
      () => setLocation('granted'),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) setLocation('denied');
        else setLocation('granted');
      },
      { timeout: 10000, maximumAge: 60_000 },
    );
  };

  const handleCompass = async () => {
    if (compass !== 'pending') return;
    const ok = await deviceHeadingBus.requestPermissionIfNeeded();
    setCompass(ok ? 'granted' : 'denied');
  };

  const handleContinue = () => {
    try {
      window.localStorage.setItem(DONE_KEY, '1');
    } catch {
      /* no-op */
    }
    setNeedsScreen(false);
  };

  return (
    <div className="fixed inset-0 z-[120] bg-turbo-900 flex items-center justify-center p-4 overflow-auto">
      <div className="max-w-md w-full bg-turbo-700/40 border border-turbo-500/30 rounded-2xl p-6 shadow-2xl my-auto">
        <div className="flex items-center gap-3 mb-4">
          <img
            src="/icons/icon-192.png?v=14ca"
            alt="All Day Turbo ULTRA"
            width={44}
            height={44}
            className="w-11 h-11 rounded-lg shrink-0"
          />
          <div>
            <div className="text-base font-semibold text-white">
              Almost ready
            </div>
            <div className="text-xs text-white/55">
              Three permissions, one tap each
            </div>
          </div>
        </div>

        <p className="text-sm text-white/80 leading-relaxed mb-5">
          All Day Turbo ULTRA needs these to work properly on the road. We'll ask
          for each below — please grant them all so the app can show your
          location, follow your heading, and ping you when new trails are
          added.
        </p>

        <div className="space-y-2.5 mb-5">
          <PermissionItem
            icon={<BellIcon />}
            title="Notifications"
            description="Get pinged when new trails, runs or tours are added."
            status={push}
            onGrant={handlePush}
            extraHint={pushError}
          />
          <PermissionItem
            icon={<PinIcon />}
            title="Location"
            description="Show your position on the map and plan routes from here."
            status={location}
            onGrant={handleLocation}
          />
          <PermissionItem
            icon={<CompassIcon />}
            title="Motion &amp; orientation"
            description="Rotate the map automatically to match where you're heading."
            status={compass}
            onGrant={handleCompass}
          />
        </div>

        <button
          type="button"
          onClick={handleContinue}
          className="w-full bg-sky-500 hover:bg-sky-400 active:bg-sky-600 text-white font-semibold rounded-lg py-3 transition-colors"
        >
          Continue
        </button>
        <p className="text-[11px] text-white/50 text-center mt-3 leading-snug">
          You can change any of these later in the Profile tab.
        </p>
        {/* Wave 14ca-fix27 — Pflicht-Links zu Impressum + Datenschutz auf
            der Erst-Aufruf-Seite. User muss sie erreichen können bevor
            er "Continue" tippt. Bewusst kleiner Text — nicht aufdringlich,
            aber klar findbar. */}
        <p className="text-[11px] text-white/40 text-center mt-4 leading-snug">
          By continuing you accept our{' '}
          <a href="/legal?tab=privacy" className="text-white/55 underline">
            privacy policy
          </a>
          . See also{' '}
          <a href="/legal?tab=imprint" className="text-white/55 underline">
            imprint
          </a>
          .
        </p>
      </div>
    </div>
  );
}

// ─── Permission Item Card ─────────────────────────────────────────────────

function PermissionItem({
  icon,
  title,
  description,
  status,
  onGrant,
  extraHint,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  status: PermStatus;
  onGrant: () => void | Promise<void>;
  /** Wave 14ca-fix64: optionaler Fehler-/Hinweis-Text (z.B. "Server hat
   *  VAPID nicht konfiguriert"). Wird unterhalb der Description angezeigt
   *  wenn gesetzt — auch wenn der Status nicht 'denied' ist. */
  extraHint?: string | null;
}) {
  // Wave 14ca-fix19: iOS-Style Toggle statt "Grant"-Button (User-Wunsch).
  // Visueller "off → on" cue ist intuitiver als ein blauer "Grant"-Button —
  // der User erkennt sofort "diese Permission ist gerade AUS, ich kann
  // sie umschalten".
  const isGranted = status === 'granted';
  const isDenied = status === 'denied';
  const isUnsupported = status === 'unsupported';
  // Disable-Logik: wenn schon granted oder unsupported, Toggle nicht
  // klickbar. 'denied' lassen wir klickbar damit der User nochmal probieren
  // kann (Browser kann re-prompten in manchen Fällen).
  const clickable = !isGranted && !isUnsupported;

  return (
    <button
      type="button"
      onClick={clickable ? () => void onGrant() : undefined}
      disabled={!clickable}
      className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${
        isGranted
          ? 'bg-emerald-500/10 border-emerald-500/30'
          : isDenied
            ? 'bg-red-500/10 border-red-500/30'
            : 'bg-turbo-800/40 border-turbo-700/50 hover:bg-turbo-800/60 active:bg-turbo-800/80'
      } ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
      aria-pressed={isGranted}
    >
      <div
        className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center ${
          isGranted
            ? 'bg-emerald-500/25 border-emerald-400/50 text-emerald-200'
            : isDenied
              ? 'bg-red-500/20 border-red-400/40 text-red-200'
              : 'bg-sky-500/20 border-sky-400/40 text-sky-200'
        }`}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-white truncate">{title}</div>
        <div className="text-[12px] text-white/60 leading-snug mt-0.5">
          {description}
        </div>
        {isDenied && !extraHint && (
          <div className="text-[11px] text-red-300/85 mt-1">
            Tap to try again — or enable in browser settings.
          </div>
        )}
        {extraHint && (
          <div
            className={`text-[11px] mt-1 leading-snug ${
              isDenied ? 'text-red-300/85' : 'text-amber-200/80'
            }`}
          >
            {extraHint}
          </div>
        )}
      </div>
      <div className="shrink-0 flex items-center">
        {isUnsupported ? (
          <span className="text-[11px] text-white/40">N/A</span>
        ) : (
          <ToggleSwitch on={isGranted} dimmed={isDenied} />
        )}
      </div>
    </button>
  );
}

/** iOS-Style Toggle. `dimmed` = Permission abgelehnt → rötlich-grau statt
 *  emerald-grün. Animiert beim Wechsel via translate-x. */
function ToggleSwitch({ on, dimmed = false }: { on: boolean; dimmed?: boolean }) {
  return (
    <span
      className={`relative inline-flex w-11 h-7 rounded-full transition-colors ${
        on
          ? 'bg-emerald-500'
          : dimmed
            ? 'bg-red-500/40'
            : 'bg-turbo-700/70'
      }`}
      aria-hidden="true"
    >
      <span
        className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${
          on ? 'translate-x-[18px]' : 'translate-x-0.5'
        }`}
      />
    </span>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function CompassIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </svg>
  );
}

